import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ComicCreate, ScrapeResponse, Tag, Person, Language, Role, LLMConfiguration } from '../../types';
import { scrapeComicData, createTag, searchTags, createPerson, searchPeople, createLanguage, searchLanguages, downloadCover, searchRoles, createRole, searchSeries, getImageUrl, cleanupCoverFile, getLLMConfigurations } from '../../api';
import { useSettings } from '../../contexts/SettingsContext';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { ISO_LANGUAGES } from '../../data/languages';

// Autocomplete component for tags (defined outside to prevent recreation)
const TagAutocompleteField: React.FC<{
  tags: string[];
  onTagsChange: (tags: string[]) => void;
}> = React.memo(({ tags, onTagsChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const searchTagsWithDelay = async () => {
      if (query.trim().length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchTags(query.trim());
        const filteredResults = results.filter(
          tag => !tags.some(selected => selected.toLowerCase() === tag.name.toLowerCase())
        );
        setSuggestions(filteredResults);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchTagsWithDelay, 300);
    return () => clearTimeout(debounceTimer);
  }, [query, tags]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleTagSelect(suggestions[selectedIndex]);
        } else if (query.trim()) {
          handleTagAdd(query.trim());
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const handleTagSelect = (tag: Tag) => {
    const newTags = [...tags, tag.name];
    onTagsChange(newTags);
    setQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleTagAdd = (tagName: string) => {
    if (!tags.some(t => t.toLowerCase() === tagName.toLowerCase())) {
      const newTags = [...tags, tagName];
      onTagsChange(newTags);
    }
    setQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleTagRemove = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    onTagsChange(newTags);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
      }
    }, 200);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 mb-2">
        {tags.map((tag, index) => (
          <span
            key={`tag-${index}-${tag}`}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleTagRemove(index)}
              className="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder={t('placeholder.searchOrAddTag')}
          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
        />
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  selectedIndex === index ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
                }`}
                onClick={() => handleTagSelect(suggestion)}
              >
                {suggestion.name}
              </button>
            ))}
            {query.trim() && !suggestions.some(s => s.name.toLowerCase() === query.toLowerCase()) && (
              <button
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 italic ${
                  selectedIndex === suggestions.length ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
                }`}
                onClick={() => handleTagAdd(query.trim())}
              >
                {t('common.add')} "{query.trim()}"
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// Autocomplete component for series (defined outside to prevent recreation)
const SeriesAutocompleteField: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = React.memo(({ value, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isUserTyping) {
      setQuery(value || '');
    }
  }, [value, isUserTyping]);

  useEffect(() => {
    const searchSeriesWithDelay = async () => {
      if (query.trim().length < 1 || !isUserTyping) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchSeries(query.trim());
        setSuggestions(results);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchSeriesWithDelay, 300);
    return () => clearTimeout(debounceTimer);
  }, [query, isUserTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setIsUserTyping(true);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSeriesSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const handleSeriesSelect = (series: string) => {
    setQuery(series);
    onChange(series);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setIsUserTyping(false);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        setIsUserTyping(false);
      }
    }, 200);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        placeholder={t('placeholder.searchOrEnterSeries')}
        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={`series-${index}-${suggestion}`}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                selectedIndex === index ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
              }`}
              onClick={() => handleSeriesSelect(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

// Individual person item component with local state to prevent focus loss
const PersonItem: React.FC<{
  initialPerson: { name: string; role?: string };
  index: number;
  onPersonChange: (index: number, person: { name: string; role?: string }) => void;
  onPersonRemove: (index: number) => void;
}> = React.memo(({ initialPerson, index, onPersonChange, onPersonRemove }) => {
  const { t } = useTranslation();
  const [localName, setLocalName] = useState(initialPerson.name);
  const [localRole, setLocalRole] = useState(initialPerson.role || '');
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Autocomplete state for person name
  const [personSuggestions, setPersonSuggestions] = useState<Person[]>([]);
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [selectedPersonIndex, setSelectedPersonIndex] = useState(-1);
  const [personInputActive, setPersonInputActive] = useState(false);
  const personInputRef = useRef<HTMLInputElement>(null);
  const personSuggestionsRef = useRef<HTMLDivElement>(null);

  // Autocomplete state for role
  const [roleSuggestions, setRoleSuggestions] = useState<Role[]>([]);
  const [showRoleSuggestions, setShowRoleSuggestions] = useState(false);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(-1);
  const [roleInputActive, setRoleInputActive] = useState(false);
  const roleInputRef = useRef<HTMLInputElement>(null);
  const roleSuggestionsRef = useRef<HTMLDivElement>(null);

  // Update local state if initial values change (e.g., from reset)
  useEffect(() => {
    setLocalName(initialPerson.name);
    setLocalRole(initialPerson.role || '');
  }, [initialPerson.name, initialPerson.role]);

  // Search people with debounce - only when input is active
  useEffect(() => {
    const searchPeopleWithDelay = async () => {
      if (!personInputActive || localName.trim().length < 1) {
        setPersonSuggestions([]);
        setShowPersonSuggestions(false);
        return;
      }

      try {
        const results = await searchPeople(localName.trim());
        setPersonSuggestions(results);
        setShowPersonSuggestions(true);
        setSelectedPersonIndex(-1);
      } catch (error) {
        
        setPersonSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(searchPeopleWithDelay, 300);
    return () => clearTimeout(debounceTimer);
  }, [localName, personInputActive]);

  // Search roles with debounce - only when input is active
  useEffect(() => {
    const searchRolesWithDelay = async () => {
      if (!roleInputActive || localRole.trim().length < 1) {
        setRoleSuggestions([]);
        setShowRoleSuggestions(false);
        return;
      }

      try {
        const results = await searchRoles(localRole.trim());
        setRoleSuggestions(results);
        setShowRoleSuggestions(true);
        setSelectedRoleIndex(-1);
      } catch (error) {
        
        setRoleSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(searchRolesWithDelay, 300);
    return () => clearTimeout(debounceTimer);
  }, [localRole, roleInputActive]);

  // Debounced update to parent
  const updateParent = React.useCallback(() => {
    onPersonChange(index, {
      name: localName,
      role: localRole || undefined
    });
  }, [index, localName, localRole, onPersonChange]);

  // Debounce parent updates
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(updateParent, 300);
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [localName, localRole, updateParent]);

  const handleNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalName(e.target.value);
    setPersonInputActive(true);
  }, []);

  const handleRoleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalRole(e.target.value);
    setRoleInputActive(true);
  }, []);

  const handleRemove = React.useCallback(() => {
    onPersonRemove(index);
  }, [index, onPersonRemove]);

  const handleNameFocus = React.useCallback(() => {
    setPersonInputActive(true);
  }, []);

  const handleNameBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Immediately update parent on blur
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    updateParent();
    
    // Hide suggestions after a delay to allow clicking on suggestions
    setTimeout(() => {
      if (!personSuggestionsRef.current?.contains(document.activeElement)) {
        setShowPersonSuggestions(false);
        setPersonInputActive(false);
      }
    }, 200);
  }, [updateParent]);

  const handleRoleFocus = React.useCallback(() => {
    setRoleInputActive(true);
  }, []);

  const handleRoleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Immediately update parent on blur
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    updateParent();
    
    // Hide suggestions after a delay to allow clicking on suggestions
    setTimeout(() => {
      if (!roleSuggestionsRef.current?.contains(document.activeElement)) {
        setShowRoleSuggestions(false);
        setRoleInputActive(false);
      }
    }, 200);
  }, [updateParent]);

  const handlePersonKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showPersonSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedPersonIndex(prev => prev < personSuggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedPersonIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedPersonIndex >= 0 && selectedPersonIndex < personSuggestions.length) {
          handlePersonSelect(personSuggestions[selectedPersonIndex]);
        }
        break;
      case 'Tab':
        if (selectedPersonIndex >= 0 && selectedPersonIndex < personSuggestions.length) {
          e.preventDefault();
          handlePersonSelect(personSuggestions[selectedPersonIndex]);
        }
        break;
      case 'Escape':
        setShowPersonSuggestions(false);
        break;
    }
  };

  const handleRoleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showRoleSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedRoleIndex(prev => prev < roleSuggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedRoleIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedRoleIndex >= 0 && selectedRoleIndex < roleSuggestions.length) {
          handleRoleSelect(roleSuggestions[selectedRoleIndex]);
        }
        break;
      case 'Escape':
        setShowRoleSuggestions(false);
        break;
    }
  };

  const handlePersonSelect = (person: Person) => {
    setLocalName(person.name);
    setShowPersonSuggestions(false);
    setSelectedPersonIndex(-1);
    roleInputRef.current?.focus();
  };

  const handleRoleSelect = (role: Role) => {
    setLocalRole(role.name);
    setShowRoleSuggestions(false);
    setSelectedRoleIndex(-1);
  };

  return (
    <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 dark:bg-gray-700 rounded">
      <div className="flex-1 flex gap-2 items-center min-w-0">
        <div className="flex-[2] relative min-w-0">
          <input
            ref={personInputRef}
            type="text"
            value={localName}
            onChange={handleNameChange}
            onKeyDown={handlePersonKeyDown}
            onFocus={handleNameFocus}
            onBlur={handleNameBlur}
            placeholder={t('llm.personName')}
            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
          />
          {showPersonSuggestions && personSuggestions.length > 0 && (
            <div
              ref={personSuggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              {personSuggestions.map((suggestion, suggestionIndex) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedPersonIndex === suggestionIndex ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
                  }`}
                  onClick={() => handlePersonSelect(suggestion)}
                >
                  <span className="block truncate">{suggestion.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 relative min-w-0">
          <input
            ref={roleInputRef}
            type="text"
            value={localRole}
            onChange={handleRoleChange}
            onKeyDown={handleRoleKeyDown}
            onFocus={handleRoleFocus}
            onBlur={handleRoleBlur}
            placeholder={t('llm.role')}
            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
          />
          {showRoleSuggestions && roleSuggestions.length > 0 && (
            <div
              ref={roleSuggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              {roleSuggestions.map((suggestion, suggestionIndex) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedRoleIndex === suggestionIndex ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
                  }`}
                  onClick={() => handleRoleSelect(suggestion)}
                >
                  <span className="block truncate">{suggestion.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={handleRemove}
        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
      >
        ×
      </button>
    </div>
  );
});

// Autocomplete component for people and roles (defined outside to prevent recreation)
const PersonRoleAutocompleteField: React.FC<{
  people: { name: string; role?: string }[];
  onPeopleChange: (people: { name: string; role?: string }[]) => void;
}> = React.memo(({ people, onPeopleChange }) => {
  const { t } = useTranslation();
  const [personQuery, setPersonQuery] = useState('');
  const [roleQuery, setRoleQuery] = useState('');
  const [personSuggestions, setPersonSuggestions] = useState<Person[]>([]);
  const [roleSuggestions, setRoleSuggestions] = useState<Role[]>([]);
  const [, setIsPersonLoading] = useState(false);
  const [, setIsRoleLoading] = useState(false);
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [showRoleSuggestions, setShowRoleSuggestions] = useState(false);
  const [selectedPersonIndex, setSelectedPersonIndex] = useState(-1);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(-1);
  const personInputRef = useRef<HTMLInputElement>(null);
  const roleInputRef = useRef<HTMLInputElement>(null);
  const personSuggestionsRef = useRef<HTMLDivElement>(null);
  const roleSuggestionsRef = useRef<HTMLDivElement>(null);

  // Search people with debounce
  useEffect(() => {
    const searchPeopleWithDelay = async () => {
      if (personQuery.trim().length < 1) {
        setPersonSuggestions([]);
        setShowPersonSuggestions(false);
        return;
      }

      setIsPersonLoading(true);
      try {
        const results = await searchPeople(personQuery.trim());
        setPersonSuggestions(results);
        setShowPersonSuggestions(true);
        setSelectedPersonIndex(-1);
      } catch (error) {
        
        setPersonSuggestions([]);
      } finally {
        setIsPersonLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchPeopleWithDelay, 300);
    return () => clearTimeout(debounceTimer);
  }, [personQuery]);

  // Search roles with debounce
  useEffect(() => {
    const searchRolesWithDelay = async () => {
      if (roleQuery.trim().length < 1) {
        setRoleSuggestions([]);
        setShowRoleSuggestions(false);
        return;
      }

      setIsRoleLoading(true);
      try {
        const results = await searchRoles(roleQuery.trim());
        setRoleSuggestions(results);
        setShowRoleSuggestions(true);
        setSelectedRoleIndex(-1);
      } catch (error) {
        
        setRoleSuggestions([]);
      } finally {
        setIsRoleLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchRolesWithDelay, 300);
    return () => clearTimeout(debounceTimer);
  }, [roleQuery]);

  const handlePersonKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showPersonSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedPersonIndex(prev => prev < personSuggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedPersonIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedPersonIndex >= 0 && selectedPersonIndex < personSuggestions.length) {
          handlePersonSelect(personSuggestions[selectedPersonIndex]);
        }
        break;
      case 'Tab':
        if (selectedPersonIndex >= 0 && selectedPersonIndex < personSuggestions.length) {
          e.preventDefault();
          handlePersonSelect(personSuggestions[selectedPersonIndex]);
        }
        break;
      case 'Escape':
        setShowPersonSuggestions(false);
        break;
    }
  };

  const handleRoleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showRoleSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedRoleIndex(prev => prev < roleSuggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedRoleIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedRoleIndex >= 0 && selectedRoleIndex < roleSuggestions.length) {
          handleRoleSelect(roleSuggestions[selectedRoleIndex]);
        } else {
          handleAddPersonRole();
        }
        break;
      case 'Escape':
        setShowRoleSuggestions(false);
        break;
    }
  };

  const handlePersonSelect = (person: Person) => {
    setPersonQuery(person.name);
    setShowPersonSuggestions(false);
    setSelectedPersonIndex(-1);
    roleInputRef.current?.focus();
  };

  const handleRoleSelect = (role: Role) => {
    setRoleQuery(role.name);
    setShowRoleSuggestions(false);
    setSelectedRoleIndex(-1);
  };

  const handleAddPersonRole = () => {
    if (personQuery.trim()) {
      const newPerson = {
        name: personQuery.trim(),
        role: roleQuery.trim() || undefined
      };
      onPeopleChange([...people, newPerson]);
      setPersonQuery('');
      setRoleQuery('');
      personInputRef.current?.focus();
    }
  };

  const handlePersonChange = React.useCallback((index: number, person: { name: string; role?: string }) => {
    const newPeople = [...people];
    newPeople[index] = person;
    onPeopleChange(newPeople);
  }, [people, onPeopleChange]);

  const handlePersonRemove = React.useCallback((index: number) => {
    const newPeople = people.filter((_, i) => i !== index);
    onPeopleChange(newPeople);
  }, [people, onPeopleChange]);

  const handlePersonInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (!personSuggestionsRef.current?.contains(document.activeElement)) {
        setShowPersonSuggestions(false);
      }
    }, 200);
  };

  const handleRoleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (!roleSuggestionsRef.current?.contains(document.activeElement)) {
        setShowRoleSuggestions(false);
      }
    }, 200);
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {people.map((person, index) => (
          <PersonItem
            key={`person-${index}`}
            initialPerson={person}
            index={index}
            onPersonChange={handlePersonChange}
            onPersonRemove={handlePersonRemove}
          />
        ))}
      </div>
      <div className="flex gap-2 items-start">
        <div className="flex-[2] relative min-w-0">
          <input
            ref={personInputRef}
            type="text"
            value={personQuery}
            onChange={(e) => setPersonQuery(e.target.value)}
            onKeyDown={handlePersonKeyDown}
            onBlur={handlePersonInputBlur}
            placeholder={t('llm.personName')}
            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 h-[30px]"
          />
          {showPersonSuggestions && personSuggestions.length > 0 && (
            <div
              ref={personSuggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              {personSuggestions.map((suggestion, index) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedPersonIndex === index ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
                  }`}
                  onClick={() => handlePersonSelect(suggestion)}
                >
                  <span className="block truncate">{suggestion.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 relative min-w-0">
          <input
            ref={roleInputRef}
            type="text"
            value={roleQuery}
            onChange={(e) => setRoleQuery(e.target.value)}
            onKeyDown={handleRoleKeyDown}
            onBlur={handleRoleInputBlur}
            placeholder={t('llm.roleOptional')}
            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 h-[30px]"
          />
          {showRoleSuggestions && roleSuggestions.length > 0 && (
            <div
              ref={roleSuggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              {roleSuggestions.map((suggestion, index) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedRoleIndex === index ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
                  }`}
                  onClick={() => handleRoleSelect(suggestion)}
                >
                  <span className="block truncate">{suggestion.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleAddPersonRole}
          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 h-[30px] flex items-center"
        >
          {t('common.add')}
        </button>
      </div>
    </div>
  );
});

// Language dropdown component (defined outside to prevent recreation)
const LanguageDropdownField: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = React.memo(({ value, onChange }) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { languages } = useReferenceData();
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLanguages = async () => {
      if (!settings) return; // Wait for settings to load
      
      try {
        setIsLoading(true);
        const allLanguages = languages;

        let filteredLanguages = allLanguages;
        
        // Filter by user's available_languages setting
        if (settings.available_languages && settings.available_languages.length > 0) {
          filteredLanguages = allLanguages.filter(lang => 
            settings.available_languages!.includes(lang.code || '')
          );
          
          // If no database languages match user preferences, create them from ISO list
          if (filteredLanguages.length === 0) {
            const missingLanguages = settings.available_languages
              .map(code => {
                const isoLang = ISO_LANGUAGES.find(iso => iso.code === code);
                return isoLang ? {
                  id: 0, // Will be created
                  name: isoLang.nativeName || isoLang.name,
                  code: isoLang.code,
                  created_at: ''
                } : null;
              })
              .filter(Boolean) as Language[];
            
            filteredLanguages = missingLanguages;
          }
        }
        
        setAvailableLanguages(filteredLanguages);
      } catch (error) {
        
        setAvailableLanguages([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (languages.length > 0) {
      loadLanguages();
    }
  }, [settings, languages]);

  const getLanguageDisplayName = (language: Language) => {
    // Try to get i18n translation first
    const translationKey = `languages.${language.code}`;
    const translatedName = t(translationKey);
    
    if (translatedName !== translationKey) {
      return translatedName;
    }
    
    // Fall back to native name from ISO list
    const isoLang = ISO_LANGUAGES.find(iso => iso.code === language.code);
    if (isoLang?.nativeName) {
      return isoLang.nativeName;
    }
    
    // Final fallback to language name
    return language.name;
  };

  const findMatchingLanguage = (inputValue: string) => {
    if (!inputValue) return '';
    
    // Try exact match first
    const exactMatch = availableLanguages.find(lang => 
      lang.name.toLowerCase() === inputValue.toLowerCase()
    );
    if (exactMatch) return exactMatch.name;
    
    // Try matching by code
    const codeMatch = availableLanguages.find(lang => 
      lang.code?.toLowerCase() === inputValue.toLowerCase()
    );
    if (codeMatch) return codeMatch.name;
    
    // Try matching display name
    const displayMatch = availableLanguages.find(lang => 
      getLanguageDisplayName(lang).toLowerCase() === inputValue.toLowerCase()
    );
    if (displayMatch) return displayMatch.name;
    
    // Try partial matches
    const partialMatch = availableLanguages.find(lang => 
      lang.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      getLanguageDisplayName(lang).toLowerCase().includes(inputValue.toLowerCase()) ||
      (lang.code && lang.code.toLowerCase().includes(inputValue.toLowerCase()))
    );
    if (partialMatch) return partialMatch.name;
    
    // If no match found, return the original value
    return inputValue;
  };

  if (isLoading) {
    return (
      <div className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700">
        {t('common.loading')}...
      </div>
    );
  }

  return (
    <select
      value={findMatchingLanguage(value) || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{t('placeholder.selectLanguage')}</option>
      {availableLanguages.map((language) => (
        <option key={language.id || language.code} value={language.name} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
          {getLanguageDisplayName(language)}
        </option>
      ))}
    </select>
  );
});


interface ScrapeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDataSelected: (data: Partial<ComicCreate>) => void;
  initialTitle?: string;
  initialSeries?: string;
  initialWriter?: string;
  availablePeople?: { name: string; role?: string }[];
}

const ScrapeModal: React.FC<ScrapeModalProps> = ({ 
  isOpen, 
  onClose, 
  onDataSelected, 
  initialTitle = '',
  initialSeries = '',
  initialWriter = '',
  availablePeople = []
}) => {
  const { t, i18n } = useTranslation();
  const { settings, llmConfigurations: contextLlmConfigurations, isLLMConfigured } = useSettings();
  // ReferenceData context is available but not currently used in this component
  useReferenceData();
  const [targetUrl, setTargetUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [scrapedData, setScrapedData] = useState<ScrapeResponse | null>(null);
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});
  const [editableData, setEditableData] = useState<ScrapeResponse>({
    tags: [],
    people: [],
    cover_image_options: []
  });
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [selectedCoverImage, setSelectedCoverImage] = useState<string>('');
  const [downloadedCovers, setDownloadedCovers] = useState<Map<string, string>>(new Map());
  const [downloadingCovers, setDownloadingCovers] = useState<Set<string>>(new Set());
  const attemptedDownloads = useRef<Set<string>>(new Set());
  const [coverImageHover, setCoverImageHover] = useState<string>('');
  const [llmConfigurations, setLlmConfigurations] = useState<LLMConfiguration[]>([]);
  const [selectedConfigurationId, setSelectedConfigurationId] = useState<number | null>(null);
  const [loadingConfigurations, setLoadingConfigurations] = useState(false);

  useEffect(() => {
    if (isOpen) {
      
      setScrapedData(null);
      setSelectedFields({});
      setEditableData({ tags: [], people: [], cover_image_options: [] });
      setSelectedCoverImage('');
      setCoverImageHover('');
      setError(null);
      setErrorDetails(null);
      setDownloadedCovers(new Map());
      setDownloadingCovers(new Set());
      attemptedDownloads.current.clear();
      
      // Load LLM configurations when modal opens
      loadLLMConfigurations();
    }
  }, [isOpen, initialWriter]);
  
  const loadLLMConfigurations = async () => {
    setLoadingConfigurations(true);
    try {
      const response = await getLLMConfigurations();
      setLlmConfigurations(response.configurations);
      
      // Set default configuration as selected if available
      const defaultConfig = response.configurations.find(config => config.is_default);
      if (defaultConfig) {
        setSelectedConfigurationId(defaultConfig.id);
      } else if (response.configurations.length > 0) {
        // If no default, select the first one
        setSelectedConfigurationId(response.configurations[0].id);
      }
    } catch (error) {
      
      setLlmConfigurations([]);
    } finally {
      setLoadingConfigurations(false);
    }
  };

  const cleanupUnusedCovers = useCallback(async (keepFilename?: string) => {
    const filesToDelete: string[] = [];
    
    // Collect all downloaded filenames except the one we want to keep
    downloadedCovers.forEach((filename, url) => {
      if (!keepFilename || filename !== keepFilename) {
        filesToDelete.push(filename);
      }
    });
    
    // Delete each file
    const deletePromises = filesToDelete.map(async (filename) => {
      try {
        await cleanupCoverFile(filename);
        
      } catch (error) {
        
      }
    });
    
    // Wait for all deletions to complete
    await Promise.all(deletePromises);
    
    // Clear the state
    setDownloadedCovers(new Map());
    setDownloadingCovers(new Set());
  }, [downloadedCovers]);
  
  // Moved this useEffect after downloadCoverForPreview is defined


  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        // Cleanup covers when pressing escape
        cleanupUnusedCovers().then(() => {
          onClose();
        });
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, cleanupUnusedCovers]);
  
  // Cleanup effect when component unmounts or modal closes
  useEffect(() => {
    return () => {
      // If the modal is being closed/unmounted and there are downloaded covers, clean them up
      if (downloadedCovers.size > 0) {
        // Use a timeout to ensure this runs after the component is unmounted
        setTimeout(() => {
          const filesToDelete: string[] = [];
          downloadedCovers.forEach((filename) => {
            filesToDelete.push(filename);
          });
          
          filesToDelete.forEach(async (filename) => {
            try {
              await cleanupCoverFile(filename);
              
            } catch (error) {
              
            }
          });
        }, 100);
      }
    };
  }, [downloadedCovers]);

  
  const getPreviewImageUrl = (url: string): string => {
    const localPath = downloadedCovers.get(url);
    if (localPath) {
      return getImageUrl(localPath);
    }
    
    return ''; // Return empty string for placeholder
  };
  
  // Effect to handle downloading covers when scraped data is available
  useEffect(() => {
    if (scrapedData) {
      const urlsToDownload = new Set<string>();
      
      if (scrapedData.cover_image_options) {
        scrapedData.cover_image_options.forEach(option => {
          urlsToDownload.add(option.url);
        });
      } else if (scrapedData.cover_image_url) {
        urlsToDownload.add(scrapedData.cover_image_url);
      }
      
      // Download each unique URL only once - inline download logic to avoid dependency issues
      urlsToDownload.forEach(async (url) => {
        // Check if we've already attempted this URL
        if (attemptedDownloads.current.has(url)) {
          return;
        }
        
        // Mark as attempted immediately to prevent retries
        attemptedDownloads.current.add(url);
        
        try {
          setDownloadingCovers(prev => new Set(prev).add(url));
          const result = await downloadCover(url);
          const localPath = result.path;
          
          setDownloadedCovers(prev => new Map(prev).set(url, localPath));
        } catch (error) {
          
          // Mark URL as failed to prevent retries
        } finally {
          setDownloadingCovers(prev => {
            const newSet = new Set(prev);
            newSet.delete(url);
            return newSet;
          });
        }
      });
    }
  }, [scrapedData]); // Only depend on scrapedData to avoid infinite loops

  // Effect to handle manual cover URL downloads for preview
  useEffect(() => {
    const manualUrl = selectedCoverImage || editableData.cover_image_url;
    if (!manualUrl) return;

    // Debounce to avoid downloading on every keystroke
    const timeoutId = setTimeout(async () => {
      // Skip if already downloaded, downloading, or if it's in the automatic download set
      if (downloadedCovers.has(manualUrl) || downloadingCovers.has(manualUrl) || attemptedDownloads.current.has(manualUrl)) {
        return;
      }

      try {
        setDownloadingCovers(prev => new Set(prev).add(manualUrl));
        const result = await downloadCover(manualUrl);
        const localPath = result.path;
        
        setDownloadedCovers(prev => new Map(prev).set(manualUrl, localPath));
        // Remove from failed covers if it was previously failed
      } catch (error) {
      } finally {
        setDownloadingCovers(prev => {
          const newSet = new Set(prev);
          newSet.delete(manualUrl);
          return newSet;
        });
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [selectedCoverImage, editableData.cover_image_url, downloadedCovers, downloadingCovers]);
  

  const categorizeError = (error: any): { message: string; details?: string } => {
    const errorMessage = error?.response?.data?.detail || error?.message || String(error);
    const statusCode = error?.response?.status;
    
    // Check for common error patterns
    if (statusCode === 401 || statusCode === 403 || errorMessage.toLowerCase().includes('api key')) {
      return { message: t('llm.authenticationError') };
    }
    
    if (statusCode === 429 || errorMessage.toLowerCase().includes('rate limit')) {
      return { message: t('llm.rateLimitError') };
    }
    
    if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('connection') || errorMessage.toLowerCase().includes('unavailable')) {
      return { message: t('llm.serviceUnavailableError') };
    }
    
    if (errorMessage.toLowerCase().includes('parse') || errorMessage.toLowerCase().includes('json')) {
      return { message: t('llm.parseError') };
    }
    
    if (errorMessage.toLowerCase().includes('llm service error')) {
      const match = errorMessage.match(/LLM service error: (.+)/);
      if (match) {
        return { message: t('llm.llmServiceError', { message: match[1] }) };
      }
    }
    
    if (errorMessage.toLowerCase().includes('configuration') || errorMessage.toLowerCase().includes('not configured')) {
      return { message: t('llm.configurationError', { message: errorMessage }) };
    }
    
    if (errorMessage.toLowerCase().includes('invalid') && errorMessage.toLowerCase().includes('url')) {
      return { message: t('llm.invalidUrlError') };
    }
    
    if (errorMessage.toLowerCase().includes('no data') || errorMessage.toLowerCase().includes('not found')) {
      return { message: t('llm.noDataFoundError') };
    }
    
    if (errorMessage.toLowerCase().includes('token limit exceeded') || errorMessage.toLowerCase().includes('cut off')) {
      return { message: t('llm.tokenLimitExceeded') };
    }
    
    if (errorMessage.toLowerCase().includes('empty response') || errorMessage.toLowerCase().includes('didn\'t return valid comic data')) {
      return { message: t('llm.noValidDataReturned') };
    }
    
    if (errorMessage.toLowerCase().includes('no comic data could be extracted')) {
      return { message: t('llm.noUsefulDataExtracted') };
    }
    
    // Default generic error with details
    return {
      message: t('llm.failedToScrapeData'),
      details: errorMessage
    };
  };

  const handleScrape = async () => {
    // Check if LLM is properly configured
    if (!isLLMConfigured()) {
      setError(t('llm.llmNotConfigured'));
      setErrorDetails(null);
      return;
    }

    const query = targetUrl;
    if (!query.trim()) {
      setError(t('llm.pleaseEnterSearchQuery'));
      setErrorDetails(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setErrorDetails(null);

    try {
      // Get the default LLM configuration
      const defaultConfig = contextLlmConfigurations.find(config => config.is_default);
      if (!defaultConfig) {
        throw new Error('No default LLM configuration found');
      }
      const model = defaultConfig.model_id;
      
      // Get user's language preferences from context
      const allowedLanguages = settings?.available_languages;
      
      const data = await scrapeComicData({
        query,
        type: 'url',
        configuration_id: selectedConfigurationId || undefined,
        model: model,
        temperature: settings?.llm_temperature,
        max_tokens: settings?.llm_max_tokens,
        allowed_languages: allowedLanguages,
        ui_language: i18n.language,
      });
      
      // Check if the response indicates a failure (backwards compatibility)
      const isFailureResponse = (
        data.tags?.includes('scraping-failed') ||
        (data.notes && (
          data.notes.includes('failed') ||
          data.notes.includes('error') ||
          data.notes.includes('Token limit exceeded')
        ))
      );
      
      // Check if response is completely empty (no useful data)
      const hasUsefulData = !!(
        data.title?.trim() ||
        data.series?.trim() ||
        data.issue_number?.trim() ||
        data.publication_date?.trim() ||
        (data.tags && data.tags.length > 0 && !data.tags.includes('scraping-failed')) ||
        (data.people && data.people.length > 0) ||
        data.cover_image_url ||
        (data.cover_image_options && data.cover_image_options.length > 0)
      );
      
      if (isFailureResponse) {
        // Treat this as an error instead of success
        throw new Error(data.notes || 'Scraping failed');
      }
      
      if (!hasUsefulData) {
        // No useful data was extracted
        throw new Error('No comic data could be extracted from the provided URL');
      }
      
      setScrapedData(data);
      setEditableData({ ...data });
      // Set initial cover image selection
      if (data.cover_image_options && data.cover_image_options.length > 0) {
        const topOption = data.cover_image_options.reduce((prev, current) => 
          (current.likelihood_score || 0) > (prev.likelihood_score || 0) ? current : prev
        );
        setSelectedCoverImage(topOption.url);
      } else if (data.cover_image_url) {
        setSelectedCoverImage(data.cover_image_url);
      }
      
      // Initialize only fields with actual data as selected
      const initialSelection: Record<string, boolean> = {};
      Object.entries(data).forEach(([key, value]) => {
        // Only select fields that have meaningful data
        const hasData = value !== null && value !== undefined && value !== '' && 
                       !(typeof value === 'string' && value.trim() === '') &&
                       (Array.isArray(value) ? value.length > 0 : true);
        initialSelection[key] = hasData;
      });
      setSelectedFields(initialSelection);

    } catch (error) {
      
      const errorInfo = categorizeError(error);
      setError(errorInfo.message);
      setErrorDetails(errorInfo.details || null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldToggle = React.useCallback((field: string) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);

  const handleFieldEdit = React.useCallback((field: string, value: string | string[] | any[]) => {
    setEditableData(prev => {
      const newData = { ...prev };
      newData[field as keyof ScrapeResponse] = value as any;
      return newData;
    });
  }, []);

  const handleUseSelected = async () => {
    setIsLoading(true);
    try {
      const selectedData: Partial<ComicCreate> = {};

      // Handle simple string fields
      Object.entries(selectedFields).forEach(([field, isSelected]) => {
        if (isSelected && editableData[field as keyof ScrapeResponse]) {
          const value = editableData[field as keyof ScrapeResponse];
          
          switch (field) {
            case 'title':
              selectedData.title = value as string;
              break;
            case 'series':
              selectedData.series = value as string;
              break;
            case 'issue_number':
              selectedData.issue_number = value as string;
              break;
            case 'publication_date':
              selectedData.publication_date = value as string;
              break;
            case 'notes':
              selectedData.notes = value as string;
              break;
          }
        }
      });

      // Handle tags - create if they don't exist
      if (selectedFields.tags && editableData.tags && editableData.tags.length > 0) {
        const tagIds: number[] = [];
        for (const tagName of editableData.tags) {
          try {
            // Search for existing tag
            const existingTags = await searchTags(tagName, 1);
            let tag: Tag;
            
            if (existingTags.length > 0 && existingTags[0].name.toLowerCase() === tagName.toLowerCase()) {
              tag = existingTags[0];
            } else {
              // Create new tag
              tag = await createTag({ name: tagName });
            }
            tagIds.push(tag.id);
          } catch (error) {
            
          }
        }
        selectedData.tag_ids = tagIds;
      }

      // Handle people - create if they don't exist, and handle their roles
      if (selectedFields.people && editableData.people && editableData.people.length > 0) {
        const personIds: number[] = [];
        const personRoles: Array<{ person_id: number; role_id: number | null }> = [];
        const personWithRolesForTransfer: Array<{ person: Person; role: Role | null }> = [];
        
        for (const personData of editableData.people) {
          try {
            // Search for existing person
            const existingPeople = await searchPeople(personData.name, 1);
            let person: Person;
            
            if (existingPeople.length > 0 && existingPeople[0].name.toLowerCase() === personData.name.toLowerCase()) {
              person = existingPeople[0];
            } else {
              // Create new person
              person = await createPerson({ name: personData.name });
            }
            
            personIds.push(person.id);
            
            // Handle the person's role if provided
            let role: Role | null = null;
            if (personData.role && personData.role.trim()) {
              try {
                // Search for existing role
                const existingRoles = await searchRoles(personData.role, 1);
                
                if (existingRoles.length > 0 && existingRoles[0].name.toLowerCase() === personData.role.toLowerCase()) {
                  role = existingRoles[0];
                } else {
                  // Create new role
                  role = await createRole({ name: personData.role });
                }
              } catch (error) {
                
                // Continue without role if role creation fails
              }
            }
            
            personRoles.push({ person_id: person.id, role_id: role?.id || null });
            personWithRolesForTransfer.push({ person, role });
          } catch (error) {
            
          }
        }
        selectedData.person_ids = personIds;
        selectedData.person_roles = personRoles;
        // Add the actual PersonWithRole objects for direct transfer
        (selectedData as any).person_with_roles = personWithRolesForTransfer;
      }


      // Handle language - always ensure proper ISO code
      if (selectedFields.language && editableData.language) {
        try {
          const languageName = editableData.language.trim();
          
          // Map AI language name to ISO language data
          let isoLang = ISO_LANGUAGES.find(iso => 
            iso.code === languageName.toLowerCase() ||
            iso.name.toLowerCase() === languageName.toLowerCase() ||
            (iso.nativeName && iso.nativeName.toLowerCase() === languageName.toLowerCase())
          );
          
          // Try common language name variants if no direct match
          if (!isoLang) {
            const languageMap: { [key: string]: string } = {
              'english': 'en', 'spanish': 'es', 'french': 'fr', 'german': 'de',
              'italian': 'it', 'portuguese': 'pt', 'dutch': 'nl', 'swedish': 'sv',
              'norwegian': 'no', 'danish': 'da', 'finnish': 'fi', 'russian': 'ru',
              'chinese': 'zh', 'japanese': 'ja', 'korean': 'ko', 'arabic': 'ar'
            };
            
            const code = languageMap[languageName.toLowerCase()];
            if (code) {
              isoLang = ISO_LANGUAGES.find(iso => iso.code === code);
            }
          }
          
          // Only proceed if we have a proper ISO language match
          if (!isoLang) {
            throw new Error(`No ISO language mapping found for: ${languageName}`);
          }
          
          // Check if this language already exists with proper ISO code
          const existingLanguages = await searchLanguages(isoLang.name, 5);
          const exactMatch = existingLanguages.find(lang => 
            lang.name === isoLang!.name && lang.code === isoLang!.code
          );
          
          let language: Language;
          if (exactMatch) {
            language = exactMatch;
          } else {
            // Create new language with proper ISO data
            language = await createLanguage({ 
              name: isoLang.name, 
              code: isoLang.code 
            });
          }
          selectedData.language_id = language.id;
          // Pass the language object directly to avoid lookup issues
          (selectedData as any).language_object = language;
          
          
          
        } catch (error) {
          
        }
      }

      // Handle cover image - use already downloaded preview or download it
      const coverUrl = selectedFields.cover_image_url ? selectedCoverImage || editableData.cover_image_url : null;
      let selectedCoverFilename: string | undefined;
      if (coverUrl) {
        try {
          // Check if we already downloaded this cover for preview
          let localPath = downloadedCovers.get(coverUrl);
          if (!localPath) {
            // Download if not already downloaded
            const coverResult = await downloadCover(coverUrl);
            localPath = coverResult.path;
          }
          selectedData.cover_image = localPath;
          selectedCoverFilename = localPath;
        } catch (error) {
          
          // Don't fail the entire operation if cover download fails
        }
      }

      
      onDataSelected(selectedData);
      
      // Cleanup unused covers (keeping the accepted one)
      await cleanupUnusedCovers(selectedCoverFilename);
      
      onClose();
    } catch (error) {
      
      const errorInfo = categorizeError(error);
      setError(errorInfo.message);
      setErrorDetails(errorInfo.details || null);
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = React.useCallback((field: string, value: any) => {
    const isSelected = selectedFields[field];
    
    // Get translated field name
    const getFieldDisplayName = (fieldName: string) => {
      const fieldTranslations: { [key: string]: string } = {
        'title': t('comic.title'),
        'series': t('comic.series'),
        'issue_number': t('llm.issueNumber'),
        'publication_date': t('llm.publicationDate'),
        'cover_image_url': t('llm.coverImageUrl'),
        'language': t('common.language'),
        'notes': t('comic.notes'),
        'tags': t('comic.tags'),
        'people': t('comic.people')
      };
      return fieldTranslations[fieldName] || fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' ');
    };
    
    const displayName = getFieldDisplayName(field);

    // Skip rendering fields that are internal/metadata
    if (field === 'cover_image_options' || field === 'source_url' || field === 'source_title') {
      return null;
    }

    return (
      <div key={field} className="border rounded-lg p-3">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id={`field-${field}`}
            checked={isSelected}
            onChange={() => handleFieldToggle(field)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded mr-2"
          />
          <label 
            htmlFor={`field-${field}`} 
            className="font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
          >
            {displayName}
          </label>
        </div>
        
        {isSelected && (
          <div>
            {field === 'cover_image_url' ? (
              <div className="space-y-3">
                {/* Cover image selection */}
                {editableData.cover_image_options && editableData.cover_image_options.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {t('llm.selectBestCover')}
                    </div>
                    <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                      {editableData.cover_image_options.map((option, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-2 border rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                            selectedCoverImage === option.url 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                          onClick={() => {
                            setSelectedCoverImage(option.url);
                            handleFieldEdit('cover_image_url', option.url);
                          }}
                        >
                          <input
                            type="radio"
                            checked={selectedCoverImage === option.url}
                            onChange={() => {
                              setSelectedCoverImage(option.url);
                              handleFieldEdit('cover_image_url', option.url);
                            }}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {option.alt_text || t('llm.coverImageOption', { number: index + 1 })}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {option.width && option.height && `${option.width}x${option.height} • `}
                              {t('llm.score')} {((option.likelihood_score || 0) * 100).toFixed(0)}%
                              {option.reason && ` • ${option.reason}`}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">
                              {option.url}
                            </div>
                          </div>
                          <div 
                            className="flex-shrink-0 w-16 h-20 border rounded overflow-hidden bg-gray-100 dark:bg-gray-800"
                            onMouseEnter={() => setCoverImageHover(option.url)}
                            onMouseLeave={() => setCoverImageHover('')}
                          >
                            {getPreviewImageUrl(option.url) ? (
                              <img
                                src={getPreviewImageUrl(option.url)}
                                alt={option.alt_text || 'Cover option'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                {downloadingCovers.has(option.url) ? t('common.loading') : '📷'}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Fallback to manual URL input if no options available */
                  <div>
                    <input
                      type="url"
                      value={selectedCoverImage || editableData.cover_image_url || ''}
                      onChange={(e) => {
                        setSelectedCoverImage(e.target.value);
                        handleFieldEdit('cover_image_url', e.target.value);
                      }}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 h-[30px]"
                      placeholder={t('llm.enterCoverImageUrl')}
                    />
                  </div>
                )}
                
                {/* Large preview on hover */}
                {coverImageHover && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 pointer-events-none">
                    <div className="max-w-md max-h-96 p-4">
                      {getPreviewImageUrl(coverImageHover) ? (
                        <img
                          src={getPreviewImageUrl(coverImageHover)}
                          alt={t('altText.coverPreview')}
                          className="max-w-full max-h-full object-contain border-4 border-white rounded shadow-lg"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-64 h-64 flex items-center justify-center text-gray-400 text-2xl bg-gray-100 border-4 border-white rounded shadow-lg">
                          {downloadingCovers.has(coverImageHover) ? t('common.loading') : '📷'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Selected cover preview */}
                {selectedCoverImage && (
                  <div className="flex justify-center">
                    {getPreviewImageUrl(selectedCoverImage) ? (
                      <img
                        src={getPreviewImageUrl(selectedCoverImage)}
                        alt={t('altText.selectedCover')}
                        className="max-w-32 max-h-48 object-contain border rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-32 h-48 flex items-center justify-center text-gray-400 text-sm bg-gray-100 border rounded">
                        {downloadingCovers.has(selectedCoverImage) ? t('common.loading') : '📷'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : field === 'tags' && Array.isArray(value) ? (
              <TagAutocompleteField
                tags={editableData.tags || []}
                onTagsChange={(newTags) => handleFieldEdit('tags', newTags)}
              />
            ) : field === 'series' ? (
              <SeriesAutocompleteField
                value={editableData.series || ''}
                onChange={(newSeries) => handleFieldEdit('series', newSeries)}
              />
            ) : field === 'language' ? (
              <LanguageDropdownField
                value={editableData.language || ''}
                onChange={(newLanguage) => handleFieldEdit('language', newLanguage)}
              />
            ) : field === 'people' && Array.isArray(value) ? (
              <PersonRoleAutocompleteField
                people={editableData.people || []}
                onPeopleChange={(newPeople) => handleFieldEdit('people', newPeople)}
              />
            ) : field === 'notes' ? (
              <textarea
                value={editableData[field as keyof ScrapeResponse] as string || ''}
                onChange={(e) => handleFieldEdit(field, e.target.value)}
                rows={3}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 h-[30px]"
                placeholder={t('placeholder.descriptionOrNotes')}
              />
            ) : (
              <input
                type={field === 'publication_date' ? 'date' : 'text'}
                value={editableData[field as keyof ScrapeResponse] as string || ''}
                onChange={(e) => handleFieldEdit(field, e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 h-[30px]"
                placeholder={field === 'language' ? t('placeholder.language') : field === 'title' ? t('placeholder.comicTitle') : ''}
              />
            )}
          </div>
        )}
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFields, editableData, selectedCoverImage, coverImageHover, handleFieldEdit, handleFieldToggle, t]);

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border dark:border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('comic.comicScrape')}</h2>
            <button
              onClick={async () => {
                await cleanupUnusedCovers(); // Cleanup all covers since none are being kept
                onClose();
              }}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
            >
              ✕
            </button>
          </div>

          {!scrapedData ? (
            <div className="space-y-4">
              {/* LLM Configuration Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('llm.configuration')}
                </label>
                {loadingConfigurations ? (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700">
                    {t('common.loading')}...
                  </div>
                ) : llmConfigurations.length > 0 ? (
                  <select
                    value={selectedConfigurationId || ''}
                    onChange={(e) => setSelectedConfigurationId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {llmConfigurations.map((config) => (
                      <option key={config.id} value={config.id} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                        {config.name} ({config.provider}) {config.is_default ? ` - ${t('common.default', 'Default')}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700">
                    {t('llm.noConfigurationsAvailable', 'No LLM configurations available. Please create one in settings.')}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('llm.url')}
                </label>
                <input
                  type="url"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder={t('llm.enterUrl')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
                  {errorDetails && (
                    <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800">
                      <p className="text-red-500 dark:text-red-400 text-xs font-medium mb-1">
                        {t('llm.errorDetails')}
                      </p>
                      <p className="text-red-500 dark:text-red-400 text-xs font-mono bg-red-100 dark:bg-red-950/50 p-2 rounded border">
                        {errorDetails}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    await cleanupUnusedCovers(); // Cleanup all covers since none are being kept
                    onClose();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleScrape}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('llm.scraping')}
                    </div>
                  ) : (
                    t('llm.scrapeData')
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <p className="text-green-600 text-sm">
                  {scrapedData.source_url ? (
                    <>
                      {t('llm.dataScrapedSuccessfullyPrefix')}{' '}
                      <a 
                        href={scrapedData.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-700 underline hover:text-green-800"
                      >
                        {scrapedData.source_url}
                      </a>
                      {t('llm.dataScrapedSuccessfullySuffix')}
                    </>
                  ) : (
                    t('llm.dataScrapedSuccessfully')
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(scrapedData).map(([field, value]) => 
                  renderField(field, value)
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={async () => {
                    await cleanupUnusedCovers(); // Cleanup all covers since going back to search
                    setScrapedData(null);
                    setSelectedCoverImage('');
                    setCoverImageHover('');
                    setError(null);
                    setErrorDetails(null);
                    attemptedDownloads.current.clear();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  {t('llm.backToSearch')}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await cleanupUnusedCovers(); // Cleanup all covers since none are being kept
                    onClose();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleUseSelected}
                  disabled={Object.values(selectedFields).every(v => !v)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {t('llm.useSelectedData')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScrapeModal;